/*
  Shared lead helper for Jwear gift box + AI chat.
  - One remembered email for all popups:   localStorage jw_lead_email
  - One "already sent to Klaviyo" record: localStorage jw_lead_subscribed = {"email","list","at"}
  - Same public Klaviyo client Subscriptions request (no duplicate posts for the same email/list).
  Usage: window.JwLead.subscribe({ email, siteId, listId, source }) → Promise<{ status }>
*/
(function () {
  'use strict';

  if (window.JwLead) return;

  var KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
  var KLAVIYO_REVISION = '2026-10-15';
  var KEY_EMAIL = 'jw_lead_email';
  var KEY_SUBSCRIBED = 'jw_lead_subscribed';
  var GIFT_SIGNED_UP = 'jw_gift_box_signed_up';
  var EMAIL_RE = /^[^\s@<>()[\]\\,;:"]{1,64}@[^\s@<>()[\]\\,;:"]+\.[a-z]{2,}$/i;
  var inflight = {};

  function get(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function set(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {}
  }

  function normalize(email) {
    return String(email || '').trim().toLowerCase();
  }

  function isValid(email) {
    var value = normalize(email);
    return value.length <= 254 && EMAIL_RE.test(value);
  }

  function getEmail() {
    var value = normalize(get(KEY_EMAIL));
    return isValid(value) ? value : '';
  }

  function readSubscribed() {
    try {
      var parsed = JSON.parse(get(KEY_SUBSCRIBED) || 'null');
      return parsed && typeof parsed.email === 'string' ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  function isSubscribed(email, listId) {
    var record = readSubscribed();
    if (!record || record.email !== normalize(email)) return false;
    return !listId || !record.list || record.list === listId;
  }

  function remember(email, options) {
    var value = normalize(email);
    if (!isValid(value)) return;
    set(KEY_EMAIL, value);
    if (options && options.subscribed) {
      set(KEY_SUBSCRIBED, JSON.stringify({ email: value, list: options.listId || '', at: Date.now() }));
    }
    try {
      document.dispatchEvent(new CustomEvent('jw:lead:email', { detail: { email: value } }));
    } catch (e) {}
  }

  function configIsValid(siteId, listId) {
    return !!listId && /^[A-Za-z0-9]{6}$/.test(siteId || '') && !/^pk_/i.test(siteId || '');
  }

  function subscribe(options) {
    var email = normalize(options && options.email);
    var siteId = String((options && options.siteId) || '').trim();
    var listId = String((options && options.listId) || '').trim();
    if (!isValid(email)) return Promise.resolve({ status: 'invalid' });
    remember(email);
    if (isSubscribed(email, listId)) return Promise.resolve({ status: 'skipped' });
    if (!configIsValid(siteId, listId)) return Promise.resolve({ status: 'disabled' });

    var key = email + '|' + listId;
    if (inflight[key]) return inflight[key];

    var controller = 'AbortController' in window ? new AbortController() : null;
    var timeout = window.setTimeout(function () {
      if (controller) controller.abort();
    }, 12000);

    inflight[key] = fetch(KLAVIYO_ENDPOINT + '?company_id=' + encodeURIComponent(siteId), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        Accept: 'application/vnd.api+json',
        revision: KLAVIYO_REVISION
      },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: (options && options.source) || 'Jwear website',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email,
                  subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
                }
              }
            }
          },
          relationships: { list: { data: { type: 'list', id: listId } } }
        }
      }),
      signal: controller ? controller.signal : undefined
    })
      .then(function (response) {
        if (response.status !== 202 && !response.ok) throw new Error('HTTP ' + response.status);
        remember(email, { subscribed: true, listId: listId });
        return { status: 'sent' };
      })
      .catch(function (error) {
        console.warn('[jw-lead] Klaviyo subscription failed.', error);
        return { status: 'failed' };
      })
      .then(function (result) {
        window.clearTimeout(timeout);
        delete inflight[key];
        return result;
      });
    return inflight[key];
  }

  /* Pick up emails entered in the gift box popup without editing its code. */
  var pendingGiftEmail = '';
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || !form.matches || !form.matches('[data-jw-gift-form]')) return;
      var input = form.querySelector('[data-jw-gift-email]');
      pendingGiftEmail = input ? normalize(input.value) : '';
    },
    true
  );
  document.addEventListener('jw:gift-box:subscribed', function () {
    if (!isValid(pendingGiftEmail)) return;
    var root = document.querySelector('[data-jw-gift]');
    var listId = root ? (root.getAttribute('data-list-id') || '').trim() : '';
    remember(pendingGiftEmail, { subscribed: true, listId: listId });
  });

  window.JwLead = {
    normalize: normalize,
    isValid: isValid,
    getEmail: getEmail,
    remember: remember,
    isSubscribed: isSubscribed,
    subscribe: subscribe,
    giftBoxSignedUp: function () {
      return get(GIFT_SIGNED_UP) === '1';
    }
  };
})();
